/* PRAVIDLA PŘEKLADU — parametry a plurály. Jedno místo pro celou appku.
 * ============================================================================
 *
 * PROČ SAMOSTATNÝ SOUBOR
 * ----------------------
 * Překlad se v repu odehrává na DVOU místech, která se nesmí rozejít:
 *   • `contexts/LanguageContext.js` → hook `t()` uvnitř Reactu (registr slovníků),
 *   • `lib/i18n/textMimoReact.js`   → čistá funkce pro jádro (přímý import slovníků).
 * Kdyby každé z nich mělo vlastní tabulku plurálů, dřív nebo později by se
 * lišila a Ukrajinec by dostal jiný tvar v grafu než v popisku pod ním.
 * Tenhle soubor je proto ZÁMĚRNĚ bez slovníků a bez Reactu — jen pravidla.
 *
 * CO TU JE
 * --------
 *   1) PARAMETRY — `dosad("Úvěr {castka} na {roky}", {…})`. Bez nich se věta
 *      skládá v JSX na kusy („Úvěr " + x + " Kč na " + y + " let") a pořadí
 *      částí je zabetonované česky. Ukrajinština i vietnamština mají slovosled
 *      jinde, takže poskládaná věta v nich nedává smysl. Se zástupnými symboly
 *      si každý jazyk pořadí určí sám ve svém slovníku.
 *   2) PLURÁLY přes `Intl.PluralRules` — viz níž, proč ne natvrdo 1 / 2–4 / 5+.
 *
 * NESAHAT: měna. Ta jde ze ZEMĚ TENANTA (`lib/tenantMena.js`), nikdy z jazyka
 * — viz `docs/MENA-A-JAZYK.md`.
 */

/** Locale JEN pro plurály. Formátování čísel zůstává české (LOCALE_BY_LANG
 *  v LanguageContext) — Ukrajinec pracující v ČR chce české oddělovače tisíců,
 *  ale ukrajinské skloňování. Jsou to dvě různé věci a schválně se nemíchají. */
export const PLURAL_LOCALE = { cs: "cs-CZ", sk: "sk-SK", uk: "uk-UA", vi: "vi-VN", en: "en-GB" };

/* Kategorie `Intl.PluralRules` → přípona klíče ve slovníku (`polozkaOne` …).
 *
 * PROČ `other` I `many` MÍŘÍ NA `Many` — ověřeno nad všemi pěti jazyky:
 *   cs, sk, uk … hlásí one, few, many, other
 *   en         … hlásí one, other
 *   vi         … hlásí JEN other (vietnamština plurál nemá)
 * Čeština a slovenština dávají 5+ jako `other` („5 položek"), ukrajinština
 * jako `many` („5 позицій") — obojí je genitiv množného čísla, tedy tentýž
 * tvar ve slovníku. Vietnamština spadne vždy do `other`, a protože má všechny
 * tři tvary stejné („mục"/"mục"/"mục"), vyjde správně ať míří kamkoli.
 * `two` a `zero` tu jsou jen jako pojistka pro budoucí jazyk; žádný z našich
 * pěti je nehlásí.
 *
 * ⚠️ POZOR NA DESETINNÁ ČÍSLA: čeština hlásí u 1,5 kategorii `many`, jenže
 * správný český tvar je „1,5 položky" (genitiv jednotného čísla), ne „1,5
 * položek". Trojice ve slovníku takový tvar nemá, proto plurály POUŽÍVEJ JEN
 * NA CELÁ ČÍSLA — počty položek, roků, kroků. Na desetinná čísla ne. */
export const PRIPONA = { one: "One", two: "Few", few: "Few", many: "Many", other: "Many", zero: "Many" };

/** Vyrobit `Intl.PluralRules` není zadarmo a volá se v seznamech na každý řádek. */
const CACHE = new Map();

/* Kdyby prostředí `Intl.PluralRules` nemělo (velmi staré jádro, ořezané ICU),
 * je lepší dát české pravidlo než spadnout. Čeština tím nic neztratí a ostatní
 * jazyky jsou na tom stejně jako před touhle prací. */
const DOSTUPNE = typeof Intl !== "undefined" && typeof Intl.PluralRules === "function";

function pravidla(lang) {
  const locale = PLURAL_LOCALE[lang] || PLURAL_LOCALE.cs;
  if (!CACHE.has(locale)) {
    try {
      CACHE.set(locale, new Intl.PluralRules(locale, { type: "cardinal" }));
    } catch {
      CACHE.set(locale, null);
    }
  }
  return CACHE.get(locale);
}

/** Nouzové české pravidlo 1 / 2–4 / 5+ — použije se jen bez `Intl.PluralRules`. */
function nouzovaKategorie(n) {
  if (n === 1) return "one";
  if (n >= 2 && n <= 4) return "few";
  return "other";
}

/**
 * Kategorie množného čísla pro dané číslo a jazyk (`one` / `few` / `many` / `other`).
 * Nečíslo i nekonečno padá na 0 — degenerovaný vstup nesmí shodit větu.
 */
export function kategoriePluralu(lang, n) {
  const cislo = Number(n);
  const bezpecne = Number.isFinite(cislo) ? cislo : 0;
  const r = DOSTUPNE ? pravidla(lang) : null;
  return r ? r.select(bezpecne) : nouzovaKategorie(bezpecne);
}

/** Přípona klíče ve slovníku (`One` / `Few` / `Many`) pro dané číslo a jazyk. */
export function priponaPluralu(lang, n) {
  return PRIPONA[kategoriePluralu(lang, n)] || "Many";
}

/**
 * Dosadí `{jmeno}` z objektu parametrů.
 *
 * Neznámý zástupný symbol se NECHÁ být — je to viditelná stopa po chybějícím
 * parametru, kdežto prázdné místo by se v textu ztratilo a nikdo by si toho
 * nevšiml. Stejně tak `{}` a `{a b}` nejsou zástupné symboly a zůstávají.
 */
export function dosad(sablona, params) {
  if (!params) return String(sablona ?? "");
  return String(sablona ?? "").replace(/\{(\w+)\}/g, (cely, jmeno) =>
    Object.prototype.hasOwnProperty.call(params, jmeno) ? String(params[jmeno]) : cely
  );
}

/**
 * Je tahle hodnota OBJEKT PARAMETRŮ, nebo fallbackový text?
 *
 * Tohle rozhoduje, jestli `t("klic", x)` bere `x` jako český fallback, nebo
 * jako hodnoty do `{zastupnych}` symbolů. Musí to být nezaměnitelné, protože
 * `t(key, fallback)` se v repu volá na tisících míst a nesmí se pohnout:
 *
 *   • řetězec, číslo, `null`, `undefined` → FALLBACK (dnešní chování)
 *   • pole                                → FALLBACK (slovník má i pole hodnot,
 *                                            viz `tools.checklist.cols`)
 *   • React element                       → FALLBACK (má `$$typeof`)
 *   • prostý objekt `{ n: 3 }`            → PARAMETRY
 *
 * Instance tříd (Date, Map, chybové objekty) parametry NEJSOU — mají vlastní
 * prototyp. Kdo chce parametry, pošle objektový literál.
 */
export function jsouParametry(hodnota) {
  if (hodnota == null || typeof hodnota !== "object") return false;
  if (Array.isArray(hodnota)) return false;
  if (hodnota.$$typeof) return false; // React element / portal / fragment
  const proto = Object.getPrototypeOf(hodnota);
  return proto === Object.prototype || proto === null;
}
